import { create } from 'zustand';
import { SupplierProductResponse } from '@api/generated/search-service';
import { supplierProductsApi } from '@api/api-config';
import { useOrgPortal } from '@hooks/useOrgPortal.ts';

interface SupplierProductFilters {
  page?: number;
  size?: number;
  query?: string;
}

interface SupplierProductState {
  products: SupplierProductResponse[];
  total: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
  hasNext: boolean;
  hasPrev: boolean;
  isLoading: boolean;
  error: string | null;
  filters: SupplierProductFilters;

  fetchProducts: (filters?: Partial<SupplierProductFilters>) => Promise<void>;
  setFilters: (filters: Partial<SupplierProductFilters>) => void;
  clearFilters: () => void;
  clearError: () => void;
}

const defaultFilters: SupplierProductFilters = {
  page: 1,
  size: 20,
};

export const useSupplierProductStore = create<SupplierProductState>(set => ({
  products: [],
  total: 0,
  totalPages: 0,
  currentPage: 1,
  pageSize: 20,
  hasNext: false,
  hasPrev: false,
  isLoading: false,
  error: null,
  filters: defaultFilters,

  fetchProducts: async (newFilters?: Partial<SupplierProductFilters>) => {
    set({ isLoading: true, error: null });

    try {
      const { orgId: supplierId, portalId } = useOrgPortal.getState();

      if (!supplierId || !portalId) {
        set({ error: 'Dodavatel nebo portál nebyl nalezen', isLoading: false });
        return;
      }

      if (newFilters) {
        set(state => ({
          filters: { ...state.filters, ...newFilters },
        }));
      }

      const currentState = useSupplierProductStore.getState();
      const filters = { ...currentState.filters, ...newFilters };

      const response = await supplierProductsApi.getSupplierProducts({
        supplierId,
        portalId,
        page: filters.page,
        size: filters.size,
        query: filters.query,
      });

      set({
        products: response.data?.items || [],
        total: response.data?.total || 0,
        totalPages: response.data?.totalPages || 0,
        currentPage: response.data?.page || 1,
        pageSize: response.data?.size || 20,
        hasNext: response.data?.hasNext || false,
        hasPrev: response.data?.hasPrev || false,
        isLoading: false,
        error: null,
      });
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Nepodařilo se načíst produkty';
      set({
        error: errorMessage,
        isLoading: false,
      });
    }
  },

  setFilters: (newFilters: Partial<SupplierProductFilters>) => {
    set(state => ({
      filters: { ...state.filters, ...newFilters, page: 1 },
    }));
  },

  clearFilters: () => {
    set({ filters: defaultFilters });
  },

  clearError: () => {
    set({ error: null });
  },
}));
